/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Form, Table, Icon, Dropdown, Popup, Accordion } from "semantic-ui-react";
import * as _ from "lodash";
import { getDuplicateEscalatorFaultBindings } from "../../utils/pdApiUtils";

type EscalatorFaultBindingsProps = {
  domainId: number;
  faultId: number;
  bindings: Api.EscalatorFaultBinding[];
  escalatorVersions: Api.EscalatorVersions;
  domains: Api.Domains;
  handleBindingsChanged: (bindings: Api.EscalatorFaultBinding[]) => void;
  permissions: boolean;
  storedbindings: Api.EscalatorFault;
  editedbindings: Api.EscalatorFault;
};

type EscalatorFaultBindingsState = {
  isOpen: boolean;
};

/** EscalatorFaultBindings component */
class EscalatorFaultBindings extends React.Component<
  EscalatorFaultBindingsProps,
  EscalatorFaultBindingsState
> {
  state = { isOpen: false };

  render(): JSX.Element {
    const { bindings, escalatorVersions, domains, permissions, storedbindings, editedbindings, domainId } = this.props;
    const { isOpen } = this.state;
    let versionOptions: any[] = [];
    const duplicateBindings = getDuplicateEscalatorFaultBindings(bindings, escalatorVersions, domainId);
    const extendedBindings = bindings.map((binding, index) => {
      if (duplicateBindings.find(duplicateBinding => duplicateBinding === index) !== undefined) {
        return { ...binding, isDuplicate: true };
      } else {
        return { ...binding, isDuplicate: false };
      }
    });

    if (editedbindings.escalatorFaultBindings) {
      editedbindings.escalatorFaultBindings.forEach((editedBinding: Api.EscalatorFaultBinding) => {
        if ((editedBinding as any).isDirty) {
          delete (editedBinding as any).isDirty;
        }
      });
    }

    // Creates escalator's version dropdown options filtered by domain
    if (isOpen) {
      versionOptions = _.values(escalatorVersions)
        .filter(escalatorVersion => escalatorVersion.domain.id === this.props.domainId)
        .map(escalatorVersion => ({
          key: escalatorVersion.id,
          text: `${escalatorVersion.number}.${domains[escalatorVersion.domain.id].enumName}`,
          value: escalatorVersion.id
        }));
      versionOptions = _.sortBy(versionOptions, ["text"]).reverse();
    }

    let showMandatory = true;
    if (storedbindings) {
      showMandatory = !_.isEqual(storedbindings, editedbindings);
    }

    return (
      <Form.Group id="escalatorFaultBindings" grouped={true}>
        <Form.Field>
          <Accordion fluid={true} styled={true}>
            <Accordion.Title
              active={isOpen}
              onClick={() => this.setState(prevState => ({ isOpen: !prevState.isOpen }))}
            >
              <Icon name="dropdown" />
              <label>
                <b>Fault bindings</b>
              </label>
              {duplicateBindings.length > 0 && (
                <p className="errorMessagePadding">Bindings should not be empty or duplicated.</p>
              )}
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
              <Table compact="very">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Version</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">Name</Table.HeaderCell>
                    {!permissions && (
                      <Table.HeaderCell>
                        <Popup
                          trigger={
                            <Icon
                              id="addEscalatorFaultBinding"
                              className="color-green"
                              link={true}
                              name="add"
                              onClick={this.handleAddBinding}
                            />
                          }
                          content={"Add binding"}
                        />
                      </Table.HeaderCell>
                    )}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {extendedBindings.map(
                    (binding, index): JSX.Element => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          <div className="flex-row">
                            {(binding as any).isDirty && showMandatory ? "*" : ""}
                            <Dropdown
                              id={`escalatorFaultBinding-${index}`}
                              compact={true}
                              fluid={true}
                              selection={true}
                              error={(binding as any).isDuplicate}
                              value={binding.escalatorVersion.id}
                              options={versionOptions}
                              onChange={(e: any, data: any) =>
                                this.handleEscalatorVersionChange(data.value, index)
                              }
                              disabled={permissions}
                            />
                          </div>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {escalatorVersions[binding.escalatorVersion.id] && (
                            <Popup
                              trigger={
                                <Icon className="color-blue" link={false} name="info circle" />
                              }
                              content={escalatorVersions[binding.escalatorVersion.id].versionName}
                            />
                          )}
                        </Table.Cell>
                        {!permissions && (
                          <Table.Cell>
                            <Popup
                              trigger={
                                <Icon
                                  className="color-red"
                                  link={true}
                                  name="remove"
                                  onClick={() => this.handleRemoveBinding(index)}
                                />
                              }
                              content={"Remove binding"}
                            />
                          </Table.Cell>
                        )}
                      </Table.Row>
                    )
                  )}
                </Table.Body>
              </Table>
            </Accordion.Content>
          </Accordion>
        </Form.Field>
      </Form.Group>
    );
  }

  // Add new binding handler
  private handleAddBinding = (): void => {
    const { faultId, bindings, handleBindingsChanged } = this.props;
    const newBinding: Api.EscalatorFaultBinding = {
      escalatorFaultId: faultId,
      escalatorVersion: {
        id: -1,
        url: ""
      }
    };
    handleBindingsChanged([newBinding, ...bindings]);
  };

  // Remove binding handler
  private handleRemoveBinding = (removedIndex: number): void => {
    const { bindings, handleBindingsChanged } = this.props;
    const modifiedBindings = bindings.filter((_, index) => index !== removedIndex);
    handleBindingsChanged(modifiedBindings);
  };

  // Change version handler
  private handleEscalatorVersionChange = (versionId: number, changedIndex: number): void => {
    const { bindings, escalatorVersions, handleBindingsChanged } = this.props;
    const escalatorVersion = escalatorVersions[versionId];
    if (escalatorVersion) {
      const editedBindings = bindings.map((binding, index) => {
        if (changedIndex === index) {
          return {
            ...binding,
            escalatorVersion: { id: escalatorVersion.id, url: escalatorVersion.url },
            isDirty: true
          };
        } else {
          return binding;
        }
      });
      handleBindingsChanged(editedBindings);
    }
  };
}

export default EscalatorFaultBindings;
